// src/components/Navbar.js — GoDavaii 2035 Premium Health OS
// ✅ ALL LOGIC UNCHANGED
// ✅ Visual tone aligned with GoDavaii AI
// ✅ Cleaner frosted dark-green navbar
// ✅ Same route hiding/search behavior preserved

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, CircleUserRound, Search, Loader2, ChevronDown } from "lucide-react";
import LocationModal from "./LocationModal";
import { useLocation } from "../context/LocationContext";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
const DEEP = "#0A5A3B";
const MID = "#0F7A53";
const ACCENT = "#18E2A1";

const HIDE_ENTIRE_NAVBAR = ["/medicines", "/search", "/all-medicines", "/ai", "/health"];

const NO_SEARCH_PATHS = [
  "/orders",
  "/profile",
  "/checkout",
  "/payment",
  "/payment-success",
  "/doctors",
  "/doctor",
  "/lab-tests",
  "/lab-partner",
];

export default function Navbar({
  search: searchProp = "",
  onSearchChange = () => {},
  onSearchEnter = () => {},
  onProfile = () => (window.location.href = "/profile"),
}) {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { currentAddress, setCurrentAddress } = useLocation();

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [search, setSearch] = useState(searchProp);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pharmacyName, setPharmacyName] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const shouldHideEntirely = HIDE_ENTIRE_NAVBAR.some((p) => routerLocation.pathname.startsWith(p));
  const hideSearch = NO_SEARCH_PATHS.some((p) => routerLocation.pathname.startsWith(p));

  const activePharmacyId = useMemo(() => {
    const m = routerLocation.pathname.match(/^\/medicines\/([a-fA-F0-9]{24})/);
    return m?.[1] || null;
  }, [routerLocation.pathname]);

  useEffect(() => {
    let cancel = false;
    async function run() {
      if (!activePharmacyId) {
        setPharmacyName("");
        return;
      }
      try {
        const r = await axios.get(`${API_BASE_URL}/api/pharmacies`, { params: { id: activePharmacyId } });
        const ph = Array.isArray(r.data) ? r.data[0] : null;
        if (!cancel) setPharmacyName(ph?.name || "");
      } catch {
        if (!cancel) setPharmacyName("");
      }
    }
    run();
    return () => {
      cancel = true;
    };
  }, [activePharmacyId]);

  const placeholder = useMemo(() => {
    if (routerLocation.pathname.startsWith("/medicines")) {
      return pharmacyName ? `Search in ${pharmacyName}` : "Search in this pharmacy";
    }
    return "Search medicines, doctors, labs...";
  }, [routerLocation.pathname, pharmacyName]);

  const handleAddressChange = (addrObj) => {
    setCurrentAddress(addrObj);
    setLocationModalOpen(false);
  };

  useEffect(() => setSearch(searchProp), [searchProp]);

  useEffect(() => {
    if (!search) {
      setOptions([]);
      setDropdownOpen(false);
      return;
    }
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      const type = routerLocation.pathname.startsWith("/medicines")
        ? "medicine"
        : routerLocation.pathname.startsWith("/doctors")
          ? "doctor"
          : routerLocation.pathname.startsWith("/labs") || routerLocation.pathname.startsWith("/lab-tests")
            ? "lab"
            : "all";

      const tryReq = async (url, params) => axios.get(url, { params, signal: controller.signal }).then((r) => r.data);

      try {
        if (type === "medicine" || type === "all") {
          const city = (currentAddress?.city || "").trim();
          const data = await tryReq(`${API_BASE_URL}/api/medicines/autocomplete`, {
            q: search,
            city,
            limit: 12,
            pharmacyId: activePharmacyId || undefined,
          });
          setOptions(data || []);
          setDropdownOpen(true);
          return;
        }

        const city = (currentAddress?.city || "").trim();
        const data = await tryReq(`${API_BASE_URL}/api/search/search-autocomplete`, {
          q: search,
          type,
          city,
        });
        setOptions(data || []);
        setDropdownOpen(true);
      } catch {
        try {
          const data = await tryReq(`${API_BASE_URL}/api/search/autocomplete`, { q: search, type });
          setOptions(data || []);
          setDropdownOpen(true);
        } catch {
          if (type === "medicine" || type === "all") {
            try {
              const meds = await tryReq(`${API_BASE_URL}/api/medicines/search`, {
                q: search,
                pharmacyId: activePharmacyId || undefined,
              });
              const names = Array.from(new Set((meds || []).map((m) => m.name))).slice(0, 10);
              setOptions(names);
              setDropdownOpen(true);
            } catch {
              setOptions([]);
              setDropdownOpen(false);
            }
          } else {
            setOptions([]);
            setDropdownOpen(false);
          }
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    const t = setTimeout(load, 160);
    return () => {
      controller.abort();
      clearTimeout(t);
    };
  }, [search, routerLocation.pathname, currentAddress?.city, activePharmacyId]);

  useEffect(() => {
    const onDown = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, []);

  const handleInput = (val) => {
    setSearch(val);
    onSearchChange(val);
  };

  const handleSelect = (val) => {
    const v = typeof val === "string" ? val : val?.label || val?.value || "";
    setSearch(v);
    setDropdownOpen(false);
    if (v) {
      const pid = activePharmacyId ? `&pharmacyId=${activePharmacyId}` : "";
      navigate(`/search?q=${encodeURIComponent(v)}${pid}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSearchEnter(search);
      if (search) {
        const pid = activePharmacyId ? `&pharmacyId=${activePharmacyId}` : "";
        navigate(`/search?q=${encodeURIComponent(search)}${pid}`);
      }
      setDropdownOpen(false);
      inputRef.current?.blur();
    }
  };

  const addressLabel = currentAddress?.formatted
    ? currentAddress.formatted.length > 32
      ? currentAddress.formatted.slice(0, 32) + "…"
      : currentAddress.formatted
    : null;

  const pageLabel = routerLocation.pathname.startsWith("/orders")
    ? "My Orders"
    : routerLocation.pathname.startsWith("/profile")
      ? "Profile"
      : routerLocation.pathname.startsWith("/checkout")
        ? "Checkout"
        : routerLocation.pathname.startsWith("/payment")
          ? "Payment"
          : routerLocation.pathname.startsWith("/ai")
            ? "GoDavaii AI"
            : routerLocation.pathname.startsWith("/health")
              ? "Health Vault"
              : routerLocation.pathname.startsWith("/doctors")
                ? "Doctors"
                : routerLocation.pathname.startsWith("/lab-tests")
                  ? "Lab Tests"
                  : routerLocation.pathname.startsWith("/lab-partner")
                    ? "Lab Partner"
                    : routerLocation.pathname.startsWith("/all-medicines")
                      ? "All Medicines"
                      : null;

  if (shouldHideEntirely) {
    return (
      <>
        <LocationModal open={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelect={handleAddressChange} />
      </>
    );
  }

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1200,
        width: "100%",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <div
        style={{
          maxWidth: 520,
          margin: "0 auto",
          background: `linear-gradient(160deg, ${DEEP} 0%, #0A4631 60%, ${MID} 100%)`,
          borderBottomLeftRadius: hideSearch ? 0 : 28,
          borderBottomRightRadius: hideSearch ? 0 : 28,
          boxShadow: "0 10px 28px rgba(10,90,59,0.18)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: -30,
            top: -30,
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(24,226,161,0.16) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: -20,
            bottom: -20,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: "0 0 auto 0",
            height: 40,
            background: "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0))",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: hideSearch ? "12px 16px 12px" : "14px 16px 10px",
            position: "relative",
          }}
        >
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            onClick={() => {
              if (window?.navigator?.vibrate) navigator.vibrate(8);
              setLocationModalOpen(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flex: 1,
              minWidth: 0,
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 14,
              padding: "8px 12px",
              cursor: "pointer",
              backdropFilter: "blur(12px)",
            }}
          >
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  background: "rgba(24,226,161,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MapPin style={{ width: 16, height: 16, color: ACCENT }} />
              </div>
              <div
                style={{
                  position: "absolute",
                  top: -1,
                  right: -1,
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  background: ACCENT,
                  border: "1.5px solid #0A4631",
                  animation: "pulse-dot 2s ease-in-out infinite",
                }}
              />
            </div>

            <div style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "rgba(24,226,161,0.88)",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  marginBottom: 1,
                }}
              >
                Delivering to
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: "#fff",
                  fontFamily: "'Sora', sans-serif",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  letterSpacing: "-0.2px",
                }}
              >
                {addressLabel || "Set delivery location"}
              </div>
            </div>

            <ChevronDown style={{ width: 14, height: 14, color: "rgba(255,255,255,0.5)", flexShrink: 0 }} />
          </motion.button>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 10, flexShrink: 0 }}>
            {pageLabel && (
              <span
                style={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.72)",
                  background: "rgba(255,255,255,0.10)",
                  padding: "4px 10px",
                  borderRadius: 100,
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                {pageLabel}
              </span>
            )}

            <motion.button
              type="button"
              whileTap={{ scale: 0.9 }}
              onClick={onProfile}
              style={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.12)",
                border: "1.5px solid rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(0,0,0,0.16)",
              }}
              aria-label="Profile"
            >
              <CircleUserRound style={{ width: 22, height: 22, color: "#fff" }} />
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {!hideSearch && (
            <motion.div
              initial={false}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              ref={boxRef}
              style={{ margin: "0 14px 14px", position: "relative", overflow: "visible" }}
            >
              <motion.div
                animate={{
                  boxShadow: searchFocused ? "0 0 0 2.5px rgba(24,226,161,0.20), 0 4px 20px rgba(0,0,0,0.14)" : "0 4px 16px rgba(0,0,0,0.10)",
                  scale: searchFocused ? 1.01 : 1,
                }}
                transition={{ duration: 0.18 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: 48,
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.97)",
                  padding: "0 14px",
                  border: "1.5px solid rgba(255,255,255,0.58)",
                }}
              >
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 10,
                    background: searchFocused ? "rgba(24,226,161,0.10)" : "#F1F5F9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 10,
                    flexShrink: 0,
                    transition: "background 0.2s",
                  }}
                >
                  {loading ? (
                    <Loader2 style={{ width: 15, height: 15, color: DEEP, animation: "spin 0.8s linear infinite" }} />
                  ) : (
                    <Search style={{ width: 15, height: 15, color: searchFocused ? DEEP : "#94A3B8" }} />
                  )}
                </div>

                <input
                  ref={inputRef}
                  value={search}
                  onChange={(e) => handleInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    setSearchFocused(true);
                    options.length > 0 && setDropdownOpen(true);
                  }}
                  onBlur={() => setSearchFocused(false)}
                  placeholder={placeholder}
                  style={{
                    flex: 1,
                    height: "100%",
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#0B1F16",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    letterSpacing: "-0.1px",
                  }}
                />

                {activePharmacyId && !loading && (
                  <span
                    style={{
                      flexShrink: 0,
                      marginLeft: 8,
                      fontSize: 10,
                      fontWeight: 700,
                      color: DEEP,
                      background: "#E8F5EF",
                      padding: "2px 8px",
                      borderRadius: 100,
                      border: "1px solid rgba(10,90,59,0.16)",
                    }}
                  >
                    This pharmacy
                  </span>
                )}
              </motion.div>

              <AnimatePresence>
                {dropdownOpen && options.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.97 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      zIndex: 1300,
                      marginTop: 8,
                      background: "#fff",
                      borderRadius: 18,
                      border: "1.5px solid rgba(12,90,62,0.10)",
                      boxShadow: "0 20px 60px rgba(0,0,0,0.16)",
                      overflow: "hidden",
                      maxHeight: 280,
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 14px 6px",
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#94A3B8",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: "1px solid #F1F5F9",
                      }}
                    >
                      Suggestions
                    </div>

                    {options
                      .map((opt) => (typeof opt === "string" ? opt : opt?.label || opt?.value || opt?.name || ""))
                      .filter((l) => l && l.trim().length > 0)
                      .filter((l, i, a) => a.indexOf(l) === i)
                      .slice(0, 10)
                      .map((label, idx) => (
                        <motion.button
                          key={`${label}-${idx}`}
                          type="button"
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSelect(label)}
                          style={{
                            display: "flex",
                            width: "100%",
                            alignItems: "center",
                            gap: 10,
                            padding: "11px 14px",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            textAlign: "left",
                            borderBottom: idx < 9 ? "1px solid #F8FAFC" : "none",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FBFA")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                        >
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 9,
                              background: "#E8F5EF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Search style={{ width: 13, height: 13, color: DEEP }} />
                          </div>
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#0B1F16",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                            }}
                          >
                            {label}
                          </span>
                        </motion.button>
                      ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <LocationModal open={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelect={handleAddressChange} />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
}